import {
  ConfluenceLogo,
  JiraServiceManagementLogo,
  JiraSoftwareLogo,
  JiraWorkManagementLogo,
  JiraProductDiscoveryLogo
} from "@atlaskit/logo";
import { fontSize } from "@atlaskit/theme";
import { B200, B400, N700 } from "@atlaskit/theme/colors";
import {
  AvailableProductsResponse as AISAvailableProductsResponse,
  SitesType,
} from "@atlassiansox/asc-stargate/lib/commonjs/src/stargate-client.types";
import {
  AsyncResult,
  ProductKey,
  useAISAvailableProducts,
  useMe,
} from "@atlassiansox/bxpkit-core";
import { SiteSelectorModal } from "@atlassiansox/bxpkit-site-selector-modal";
import React, { useEffect, useRef, useState } from "react";
import { IntlProvider } from "react-intl";
import styled from "styled-components";
import _ from "underscore";
import Environment from "../../../common/environment";
import storage from "../../../utils/storage";
import tracking from "../../../utils/tracking";

const Subheadline = styled.div`
  margin-top: 10px;
  font-size: ${fontSize.h400};
`;

const SelectedTemplate = styled.span`
  font-weight: bold;
`;

const logoHeader = (props) => {
  let logo;
  const logoProps = {
    size: "medium",
    textColor: N700,
    iconColor: B400,
  };
  const confluenceLogoProps = {
    size: "medium",
    textColor: N700,
    iconColor: N700,
    iconGradientStart: B400,
    iconGradientStop: B200,
  };
  switch (props.productKey) {
    case ProductKey.JIRA_SOFTWARE:
      logo = <JiraSoftwareLogo {...logoProps} label="Jira Software logo" />;
      break;
    case ProductKey.JIRA_PRODUCT_DISCOVERY:
      logo = (
        <JiraProductDiscoveryLogo
          {...logoProps}
          label="Jira Product Discovery logo"
        />
      );
      break;
    case ProductKey.JIRA_SERVICE_MANAGEMENT:
      logo = (
        <JiraServiceManagementLogo
          {...logoProps}
          label="Jira Service Management logo"
        />
      );
      break;
    case ProductKey.JIRA_SERVICE_DESK:
      logo = (
        <JiraServiceManagementLogo
          {...logoProps}
          label="Jira Service Management logo"
        />
      );
      break;
    case ProductKey.JIRA_CORE:
      logo = (
        <JiraWorkManagementLogo
          {...logoProps}
          label="Jira Work Management logo"
        />
      );
      break;
    case ProductKey.CONFLUENCE:
    default:
      logo = (
        <ConfluenceLogo {...confluenceLogoProps} label="Confluence logo" />
      );
      break;
  }
  return (
    <div>
      {logo}
      <Subheadline>
        {props.selectedTemplate ? props.selectedTemplate : "You selected"}:{" "}
        <SelectedTemplate>{props.templateName}</SelectedTemplate>
      </Subheadline>
    </div>
  );
};

type UserData = {
  aaid: string;
  displayName: string;
  avatar: string;
  email: string;
  userSites: SitesType[];
  isLoggedIn: boolean;
};

type TemplateEventData = Event & {
  dataset?: {
    templateKey: string;
    templateId: string;
    bluePrintModule: string;
    templateName: string;
    productKey: string;
  };
};

export default function ProductTemplateSiteSelectorContainer(props: {
  id: string;
  product: string;
  featureFlags?: object;
  labels: any;
  prefix: string;
  redirectToIdentity: string;
  internalLinkAfterIdentity: string;
  cloudSignupLink: string;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [learnMore, setLearnMore] = useState(false);
  const [bluePrintModule, setBluePrintModule] = useState("");
  const [productKey, setProductKey] = useState<string>(props.product);
  const [userData, setUserData] = useState<UserData | null>(null);
  const templateName = useRef("");
  const deferredEvent = useRef<any>(null);
  const productSites = useRef<string[]>([]);
  const shouldUseOnlyFreePlans = useRef<boolean>(false);

  const meDataAsync = useMe();
  const aisAvailableProducts = useAISAvailableProducts({});

  const getEventName = (eventName: string): string => {
    return `${props.prefix}.${eventName}`;
  };

  const getOriginSource = () => {
    let queryParamsValue = '';
    let params = (new URL(window.location)).searchParams;
    if(params.has("originSource")) {
      const originSourceValue = params.get("originSource");
      queryParamsValue = `&originSource=${originSourceValue}`;
    }
    return queryParamsValue;
  };

  const openModalFromQueryParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const templateId = decodeURIComponent(urlParams.get("templateModal"));
    if (templateId) {
      const modalLinks = [
        ...document.querySelectorAll(
          ".component--template-library__cta__action.link-arrow-out"
        ),
      ];
      modalLinks.find((links) => {
        const dataSet = links.dataset;

        if (templateId === dataSet.templateId) {
          const event: TemplateEventData = document.createEvent("Event");
          event.dataset = {
            templateKey: dataSet.templateId,
            templateId: dataSet.templateId,
            bluePrintModule: dataSet.templateId,
            templateName: dataSet.templateName,
            productKey: dataSet.productKey,
          };
          event.initEvent("productTemplateModal", true, true);
          document.body.dispatchEvent(event);
          return true;
        }
      });
    }
  };

  const getProductSites = (
    sites: SitesType[],
    filterProductKey: string,
  ) => {
    return _.filter(sites, (site) =>
      site.products.includes(filterProductKey)
    ).map((site) => {
      if (shouldUseOnlyFreePlans.current) {
        // filter out sites that contain premium products
        const hasPremiumProduct = site.productsAndEditions && site.productsAndEditions.some((product) => {
          return product.productEdition === "premium"
        });
        if (!hasPremiumProduct) {
          return site.url;
        }
        return undefined
      } else {
        return site.url;
      }
      }).filter((site) => site !== undefined);
    };

  const isJiraTemplate = (checkProduct: string): boolean => {
    return [
      ProductKey.JIRA_SOFTWARE,
      ProductKey.JIRA_PRODUCT_DISCOVERY,
      ProductKey.JIRA_SERVICE_MANAGEMENT,
      ProductKey.JIRA_SERVICE_DESK,
      ProductKey.JIRA_CORE,
    ].includes(checkProduct as ProductKey);
  };

  /*
  Backend needs product and templateId.
  templateId is actually blueprint module key but BE still expects the key to be templateId
 */
  const getCloudSignupUrl = (blueprintModuleKey: string) => {
    let url = `${props.cloudSignupLink}?product=${productKey}&templateId=${blueprintModuleKey}&edition=free`;
    if (isJiraTemplate(productKey)) {
      const urlParams = new URLSearchParams(window.location.search);
      if (!urlParams.has("p")) {
        urlParams.append("templateModal", blueprintModuleKey);
      }
      url += `&continue=${encodeURIComponent(
        `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`
      )}`;
    } else if (productKey === ProductKey.CONFLUENCE) {
      const continueUrl = `${Environment.getWacIntegrationUrl()}${
        props.internalLinkAfterIdentity
      }?templateId=${
        blueprintModuleKey +
        encodeURIComponent(
          "&templateName=" + encodeURIComponent(templateName.current)
        )
      }${getOriginSource()}`;
      url = `${url}&continue=${encodeURIComponent(continueUrl)}`;
    }
    return url;
  };

  const getIdentityUrl = (blueprintModuleKey: string, templateName: string) => {
    const loginUrl = `${Environment.getIdentityBaseUrl()}/login?`;
    const continueUrl = `${Environment.getWacIntegrationUrl()}${
      props.internalLinkAfterIdentity
    }?templateName=${encodeURIComponent(
      templateName
    )}&templateId=${blueprintModuleKey}${getOriginSource()}`;
    return `${loginUrl}&continue=${encodeURIComponent(continueUrl)}`;
  };

  /*
   * For phase I of this feature, we are using the redirect option of the API, as there are CORS issues with doing AJAX calls
   * In phase II, we can alter stargate, so that we can do an AJAX call to confluence staging/production instances
   */
  const getCloudExistingUrl = (siteUrl: string, bluePrintModule: string) => {
    let referer = "wacdirect";
    if (learnMore) {
      referer = "waclearnmore";
    }
    let url = `${siteUrl}/wiki/login.action?os_destination=%2Frest%2Fcreate-dialog%2F1.0%2Fcontent%2Fblueprint%2FgetGlobalFabricBlueprintDraft%3Fredirect%3Dtrue%26blueprintModule%3D${bluePrintModule}%26referer%3D${referer}`;

    if (productKey !== ProductKey.CONFLUENCE) {
      url = `${siteUrl}/jira/create-project?templateId=${templateId}`;
    }
    
    url += `${getOriginSource()}`;

    return url;
  };

  const processUserFlow = () => {
    if (deferredEvent.current) {
      useTemplateClickHandler(deferredEvent.current);
      deferredEvent.current = null;
    }
  };

  const useTemplateClickHandler = (element) => {
    const templateId = element.dataset ? element.dataset.templateKey : "";
    const bluePrintModule = element.dataset
      ? element.dataset.bluePrintModule
      : "";
    templateName.current = element.dataset ? element.dataset.templateName : "";

    if (productSites.current && productSites.current.length) {
      setIsOpen(true);
      setTemplateId(templateId);
      setBluePrintModule(bluePrintModule);
    } else {
      if (props.redirectToIdentity && props.internalLinkAfterIdentity) {
        window.location.href = getIdentityUrl(
          bluePrintModule,
          templateName.current
        );
      } else {
        window.location.href = getCloudSignupUrl(bluePrintModule);
      }
    }
  };

  const onProductTemplateModalEvent = (event) => {
    if (!event.dataset) {
      return;
    }
    const siteUrls = getProductSites(
      userData?.userSites || [],
      event.dataset.productKey
    );

    setProductKey(event.dataset.productKey);
    productSites.current = siteUrls;

    if (siteUrls.length > 0) {
      useTemplateClickHandler(event);
    } else {
      deferredEvent.current = event;
    }
  };

  const clickedUseTemplateEvent = (event) => {
    const element = event.target;
    const templateName = element.dataset ? element.dataset.templateName : "";
    const templateId = element.dataset ? element.dataset.templateKey : "";
    const anonymousId = storage.getCookie("ajs_anonymous_id") || "unknown";
    const productKey =
      element.dataset && element.dataset.productKey
        ? element.dataset.productKey
        : ProductKey.CONFLUENCE;
    const event_class = element.classList.value || "";
    const label = element.innerText || "";
    const eventComponent = "linkButton";

    tracking.trackEvent(getEventName("clicked-use-template"), {
      templateName,
      label,
      path: window.location.pathname,
      url: window.location.href,
      eventComponent,
      event_class,
      event_type: "ui",
      anonymousId,
      product: productKey,
      templateId,
      loginUserDetected: userData?.isLoggedIn,
    });
  };

  const onClick = (event: any) => {
    if (props.id && event.target.id !== props.id) {
      return;
    }

    if (
      event.target.classList.contains(
        "component--template-library__cta__action"
      )
    ) {
      const element = event.target;
      clickedUseTemplateEvent(event);
      useTemplateClickHandler(element);
    }
    // try now button click tracking
    if (
      event.target.classList.value.includes(
        "doNotAutoTrack standalone-template-button button button--regular button--one button--primary"
      )
    ) {
      clickedUseTemplateEvent(event);
    }
  };

  const onSiteSelected = (siteUrl: string) => {
    tracking.trackEvent(getEventName("submitted-existing-site"), {
      site: siteUrl,
      bluePrintModule,
      eventName: "project-created-(client)",
      createdUsing: "templateWac",
      userId: userData?.aaid,
      product: productKey,
      learnMore,
    });

    if (userData) {
      window.open(getCloudExistingUrl(siteUrl, bluePrintModule), "_blank");
      setIsOpen(false);
    }
  };

  const onNewSiteSelected = (sameWindow?: boolean) => {
    tracking.trackEvent(getEventName("submitted-new-site"), {
      bluePrintModule,
      learnMore,
    });

    setIsOpen(false);
    if (sameWindow) {
      window.location.href = getCloudSignupUrl(bluePrintModule);
    } else {
      window.open(getCloudSignupUrl(bluePrintModule), "_blank");
    }
  };

  const onSiteChanged = () => {
    tracking.trackEvent(getEventName("changed-selected-site"), {});
  };

  const onSwitchAccountClick = () => {
    tracking.trackEvent(getEventName("clicked-switch-account"), {});
    if (productKey === ProductKey.CONFLUENCE) {
      const continueUrl = `${Environment.getWacIntegrationUrl()}${
        props.internalLinkAfterIdentity
      }?templateId=${bluePrintModule}&templateName=${encodeURIComponent(
        templateName.current
      )}`;
      window.location.href = Environment.getSwitchAccountURL(continueUrl);
    } else {
      window.location.href = Environment.getSwitchAccountURL();
    }
  };

  const onClose = () => {
    tracking.trackEvent(getEventName("clicked-modal-closed"), {});
    setIsOpen(false);
    window.history.pushState(
      null,
      "",
      window.location.search.replace(/&p=(.*?)+/g, "")
    );
  };

  useEffect(() => {
    const learnMoreNode = document.getElementById("useFullPageLayout");
    if (learnMoreNode !== null) {
      setLearnMore(true);
      setBluePrintModule(learnMoreNode.innerText);
      tracking.trackEvent(getEventName("template-page-viewed"), {
        learnMore,
        bluePrintModule,
      });
    } else {
      tracking.trackEvent(getEventName("template-page-viewed"), {
        learnMore,
      });
    }

    // If on ITSM basic template page, don't display sites that contain premium products
    // this template is only available on free plans
    if (
      !!document.querySelector(
      '.component--template-library__cta__action[data-template-id="com.atlassian.servicedesk:simplified-it-service-management-basic"]')
    ) {
      shouldUseOnlyFreePlans.current = true;
    }
  }, []);

  useEffect(() => {
    if (
      AsyncResult.isPending(meDataAsync) ||
      AsyncResult.isPending(aisAvailableProducts)
    ) {
      return;
    }

    setIsLoading(false);

    const meData = meDataAsync.catch(() => {
      tracking.trackEvent(getEventName("user.getusererror"), {});
      return null;
    }).value;
    const sitesData = aisAvailableProducts.catch(() => null).value;
    if (!meData || !sitesData) {
      return;
    }

    productSites.current = getProductSites(sitesData.sites, productKey);
    setUserData({
      aaid: meData.accountId,
      displayName: meData.name,
      avatar: meData.picture,
      email: meData.email || "",
      userSites: sitesData.sites,
      isLoggedIn: true,
    });

    tracking.trackEvent(getEventName("user.loggedin"), {});
    openModalFromQueryParams();
    processUserFlow();
  }, [meDataAsync, aisAvailableProducts]);

  document.addEventListener("click", onClick, false);
  document.addEventListener(
    "productTemplateModal",
    onProductTemplateModalEvent,
    false
  );

  return isOpen && !isLoading ? (
    <IntlProvider
      locale={document.documentElement.lang}
      messages={props.labels}
    >
      <SiteSelectorModal
        header={logoHeader({
          productKey,
          templateName: templateName.current,
          selectedTemplate: props.labels["SiteSelectorModal.selectedTemplate"],
        })}
        userName={userData?.displayName || ""}
        userEmail={userData?.email || ""}
        isOpen={isOpen}
        sites={productSites.current}
        onSiteChanged={onSiteChanged}
        onSiteSelected={onSiteSelected}
        onNewSiteSelected={onNewSiteSelected}
        onSwitchAccountClick={onSwitchAccountClick}
        onModalClose={onClose}
        avatarSrc={userData?.avatar}
      />
    </IntlProvider>
  ) : null;
}
